import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listContacts } from '@/api/people'
import {
  updatePersonalLoan,
  type InterestBasis,
  type PersonalLoan,
  type UpdatePersonalLoanPayload,
} from '@/api/personalLoans'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { MODAL_BOX, MODAL_OVERLAY } from '@/components/transactions/TransactionForms'
import { LABEL_CLS, errorDetail, invalidateAfterLoanWrite } from './shared'
import { InterestFields } from './InterestFields'
import { rateError } from '@/lib/personalLoans'

export function EditTermsModal({ loan, onClose }: { loan: PersonalLoan; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [contactId, setContactId] = useState(loan.contact.id)
  const [basis, setBasis] = useState<InterestBasis>(loan.interestBasis)
  const [rateText, setRateText] = useState(loan.interestRate?.toString() ?? '')
  const [dueDate, setDueDate] = useState(loan.dueDate ?? '')
  const [notes, setNotes] = useState(loan.notes ?? '')
  const [rateErr, setRateErr] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: false }],
    queryFn: () => listContacts(),
  })
  const people = contacts.some((c) => c.id === loan.contact.id)
    ? contacts
    : [{ id: loan.contact.id, name: loan.contact.name }, ...contacts]

  const mutation = useMutation({
    mutationFn: (payload: UpdatePersonalLoanPayload) => updatePersonalLoan(loan.accountId, payload),
    onSuccess: () => {
      invalidateAfterLoanWrite(queryClient)
      onClose()
    },
    onError: (err) => setSubmitError(errorDetail(err, 'Something went wrong. Please try again.')),
  })

  function submit() {
    const rErr = rateError(basis, rateText)
    setRateErr(rErr)
    if (rErr) return
    mutation.mutate({
      contactId,
      interestBasis: basis,
      ...(basis !== 'NONE' ? { interestRate: Number(rateText.trim()) } : {}),
      ...(dueDate ? { dueDate } : {}),
      notes: notes.trim(),
    })
  }

  return (
    <div className={MODAL_OVERLAY}>
      <div className={MODAL_BOX} role="dialog" aria-label="Edit terms">
        <h2 className="mb-4 text-lg font-bold text-[var(--fl-text-1)]">Edit terms</h2>
        <div className="space-y-4">
          <div>
            <label className={LABEL_CLS}>Person</label>
            <Select aria-label="Person" value={contactId} onChange={setContactId}>
              {people.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <InterestFields
            basis={basis}
            rateText={rateText}
            onBasisChange={setBasis}
            onRateChange={setRateText}
            rateError={rateErr}
          />
          <div>
            <label htmlFor="terms-due-date" className={LABEL_CLS}>
              Due date (optional)
            </label>
            <DatePicker id="terms-due-date" value={dueDate} onChange={setDueDate} />
          </div>
          <div>
            <label htmlFor="terms-notes" className={LABEL_CLS}>
              Reason / notes
            </label>
            <Textarea
              id="terms-notes"
              rows={2}
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {submitError && <p className="text-xs text-[var(--fl-error)]">{submitError}</p>}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={submit} disabled={mutation.isPending} className="flex-1 font-semibold">
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
